import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Field } from "@design-system/Field/Field";
import { MonetaryInput } from "@design-system/MonetaryInput/MonetaryInput";
import { Form, useFormikContext } from "formik";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useAccounts } from "@hooks/useAccounts";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { FinancialAccount } from "@common/types/financialAccount";
import { Entity } from "@common/types/entity";
import { useMemo } from "react";
import { format, parseISO } from "date-fns";
import { getFinancialAccountOpeningBalanceDate } from "@common/helpers/openingBalance";
import { fullDateLongFormat } from "@common/helpers/date";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { Loader } from "@design-system/Loader/Loader";
import { MainOpeningBalance } from "@common/schemas/domains/accounting/opening-balance.schema";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { OpeningBalanceJournalEntryPreview } from "./OpeningBalanceJournalEntryPreview";
import styles from "./FinancialAccountOpeningBalance.module.scss";
import {
    OpeningBalanceForm,
    shouldCreateJournalEntry as shouldCreateJournalEntryHelper,
} from "./helpers";

const fields = createFormikFields<OpeningBalanceForm>();

interface Props {
    financialAccount: FinancialAccount;
    entity: Entity;
    existingOpeningBalance?: MainOpeningBalance | null;
    activityExcludingMainOpeningBalance: number;
    onClose: () => void;
    isLoading: boolean;
    isSaving: boolean;
    isOpeningBalanceLocked: boolean;
}

export const FinancialAccountOpeningBalanceModalContent: React.FC<Props> = ({
    financialAccount,
    entity,
    onClose,
    isSaving,
    isLoading,
    existingOpeningBalance,
    activityExcludingMainOpeningBalance,
    isOpeningBalanceLocked,
}) => {
    const { isLoading: accountsLoading, accounts } = useAccounts({
        entityId: entity.id,
    });
    const showAccountNumbers = useShowAccountNumbers();
    const { values } = useFormikContext<OpeningBalanceForm>();

    const accountInLedger = useMemo(
        () =>
            accounts.find(
                (account) => account.financialAccountId === financialAccount.id,
            ),
        [accounts, financialAccount.id],
    );

    const openingBalanceDateString = useMemo(
        () =>
            format(
                parseISO(
                    existingOpeningBalance?.date ??
                        getFinancialAccountOpeningBalanceDate(
                            entity,
                            financialAccount,
                        ),
                ),
                fullDateLongFormat,
            ),
        [entity, financialAccount, existingOpeningBalance?.date],
    );

    const startDateString = useMemo(
        () =>
            format(
                parseISO(
                    financialAccount.bookkeepingStartDate ??
                        entity.bookkeepingStartDate,
                ),
                fullDateLongFormat,
            ),
        [entity, financialAccount],
    );

    const balanceAsOfStartDate = useMemo(
        () =>
            sumMonetaryValues([
                activityExcludingMainOpeningBalance,
                values.openingBalance ?? 0,
                values.adjustment ?? 0,
            ]),
        [
            activityExcludingMainOpeningBalance,
            values.openingBalance,
            values.adjustment,
        ],
    );

    const shouldCreateJournalEntry = shouldCreateJournalEntryHelper(values);

    const ctaText = useMemo(() => {
        if (!shouldCreateJournalEntry) {
            return "Save";
        }
        if (existingOpeningBalance) {
            return "Save journal entry";
        }
        return "Create journal entry";
    }, [shouldCreateJournalEntry, existingOpeningBalance]);

    if (accountsLoading || isLoading) {
        return <Loader />;
    }

    return (
        <Form>
            <div className={styles.form}>
                <fields.Money
                    name="openingBalance"
                    label={`Opening Balance as of ${openingBalanceDateString}`}
                    className={styles.amountField}
                    clearable
                    emptyValue={undefined}
                    size="sm"
                    disabled={isOpeningBalanceLocked}
                />
                <fields.LedgerAccount
                    name="clearingAccountId"
                    label="Balancing account"
                    className={styles.accountField}
                    entityId={entity.id}
                    showAccountCode={showAccountNumbers}
                    suggestedType={AccountType.equity}
                    disabled={
                        !shouldCreateJournalEntry || isOpeningBalanceLocked
                    }
                    buttonProps={{ size: "xl" }}
                />
                {!!activityExcludingMainOpeningBalance && (
                    <Field
                        label={`Transactions before ${startDateString}`}
                        className={styles.amountField}
                    >
                        <MonetaryInput
                            value={activityExcludingMainOpeningBalance}
                            onValueChange={() => {}}
                            size="sm"
                            disabled
                        />
                    </Field>
                )}
            </div>
            <div className={styles.form}>
                <fields.Money
                    name="adjustment"
                    label="Adjustment"
                    className={styles.amountField}
                    clearable
                    emptyValue={undefined}
                    size="sm"
                />
                <fields.LedgerAccount
                    name="adjustmentClearingAccountId"
                    label="Adjustment balancing account"
                    className={styles.accountField}
                    entityId={entity.id}
                    showAccountCode={showAccountNumbers}
                    suggestedType={AccountType.equity}
                    disabled={!values.adjustment}
                    buttonProps={{ size: "xl" }}
                />
                <Field
                    label={`Balance as of ${startDateString}`}
                    className={styles.amountField}
                >
                    <MonetaryInput
                        value={balanceAsOfStartDate}
                        onValueChange={() => {}}
                        size="sm"
                        disabled
                    />
                </Field>
            </div>
            <OpeningBalanceJournalEntryPreview
                isOpeningBalanceLocked={isOpeningBalanceLocked}
                existingOpeningBalance={existingOpeningBalance}
                accountInLedger={accountInLedger}
                entity={entity}
                financialAccount={financialAccount}
                accounts={accounts}
                shouldCreateJournalEntry={shouldCreateJournalEntry}
            />

            <footer className={styles.footer}>
                <Button
                    variant="secondary"
                    className={styles.cancelButton}
                    onClick={() => onClose()}
                    size="xl"
                    disabled={isSaving}
                >
                    Cancel
                </Button>

                {!shouldCreateJournalEntry && (
                    <div>
                        No journal entry created for $0.00 opening balance.
                    </div>
                )}
                <ButtonWithLoader
                    variant="primary"
                    loading={isSaving}
                    type="submit"
                    size="xl"
                >
                    {ctaText}
                </ButtonWithLoader>
            </footer>
        </Form>
    );
};
